import dayjs from '@skynet/formatting-date';

import type { JsonTransaction, TransactionObj } from 'src/store/shared/transactions/types';
import type { JsonBalanceHistoryItem } from 'src/store/shared/balance-history/types';

import type { NumberFormatterOptions } from '@skynet/sky-number';
import { formatNumber } from '@skynet/sky-number';

import type { GetTransactionsInRangeParams } from '../core/types';

const defaultFormat: NumberFormatterOptions = {
  style: 'currency',
  format: 'precision' as const,
};

export const getTransactionsInRange = ({
  transactions,
  accountId = undefined,
  token = undefined,
  startDate,
  endDate,
}: GetTransactionsInRangeParams) => {
  const mStartDate = dayjs(startDate).startOf('day');
  let mEndDate = dayjs(endDate);

  if (mEndDate.isAfter(dayjs())) {
    mEndDate = dayjs();
  }

  return transactions.filter((transaction) => {
    // skip automatic transactions
    if (transaction.auto) {
      return false;
    }

    // skip if transaction is not for the target account
    if (accountId && transaction.accountID !== accountId) {
      return false;
    }

    // skip if transaction is not for the target token
    if (token && transaction.currency?.toLowerCase() !== token?.toLowerCase()) {
      return false;
    }

    // skip if transaction is not in the range
    const mTransaction = dayjs(transaction.date, 'YYYY-MM-DD HH:mm');

    return !(mTransaction.isAfter(mEndDate) || mTransaction.isBefore(mStartDate));
  });
};

export const getHistoryWithTransactions = (
  historyItems: JsonBalanceHistoryItem[],
  transactions: JsonTransaction[] = [],
) => {
  const historyWithTransactionsMap: Record<string, JsonBalanceHistoryItem> = {};

  // update history with transactions
  historyItems.forEach((item) => {
    const date = dayjs(item?.date).format('YYYY-MM-DD HH:mm');
    const transaction = transactions.find((t) => t.date === item?.date);

    if (transaction) {
      historyWithTransactionsMap[date] = {
        ...item,
      };
    } else {
      historyWithTransactionsMap[date] = item;
    }
  });

  // convert other transactions to history items
  transactions.forEach((transaction) => {
    const date = dayjs(transaction?.date).format('YYYY-MM-DD HH:mm');

    if (!historyWithTransactionsMap[date]) {
      historyWithTransactionsMap[date] = {
        accountID: transaction.accountID,
        date: transaction.date,
        type: /USDT?/.test(transaction.currency) ? 0 : 1,
        token: transaction.currency,
        lastPrice: transaction.usd / transaction.volume,
        quotePrice: transaction.usd / transaction.volume,
        entryPrice: 0,
        leverage: 0,
        marginBalance: 0,
        unrealizedPnL: 0,
        volume: transaction.volume,
      };
    }
  });

  return Object.values(historyWithTransactionsMap);
};

export const getTransactionSummary = (
  transactions: JsonTransaction[],
  startDate?: string,
  endDate?: string,
) => {
  if (!startDate || !endDate) {
    return {};
  }

  const transactionsInRange = getTransactionsInRange({
    transactions,
    startDate,
    endDate,
  });

  return transactionsInRange.reduce<TransactionObj>((acc, transaction) => {
    const key = `${transaction.accountID}_${transaction.currency}`;
    const type = transaction.type;
    if (!acc[key]) {
      acc[key] = {
        [type]: {
          volume: 0,
          volumeUSD: 0,
        },
      };
    }

    acc[key][type] = {
      volume: (acc[key][type]?.volume ?? 0) + transaction.volume,
      volumeUSD: (acc[key][type]?.volumeUSD ?? 0) + transaction.usd,
      currency: transaction.currency,
      date: transaction.date,
    };

    return acc;
  }, {});
};

/**
 * Aggregate totals per token across all accounts. Returns only `summary_<token>` keys.
 * Ignores any existing `summary_` or `selected-summary_` keys in the input.
 */
export const aggregateTransactionTotals = (objOfTransaction: TransactionObj): TransactionObj => {
  const result: TransactionObj = {};

  Object.entries(objOfTransaction).forEach(([key, transaction]) => {
    if (!key) return;
    if (key.startsWith('summary_') || key.startsWith('selected-summary_')) return;

    const parts = key.split('_');
    const token = parts.slice(1).join('_');
    if (!token) return;

    const summaryKey = `summary_${token}`;

    if (!result[summaryKey]) {
      result[summaryKey] = {
        Deposit: { volume: 0, volumeUSD: 0, currency: token },
        Withdraw: { volume: 0, volumeUSD: 0, currency: token },
      };
    }

    const summary = result[summaryKey];
    if (transaction?.Deposit && summary?.Deposit) {
      summary.Deposit.volumeUSD =
        (summary.Deposit.volumeUSD ?? 0) + (transaction.Deposit.volumeUSD ?? 0);
      summary.Deposit.volume = (summary.Deposit.volume ?? 0) + (transaction.Deposit.volume ?? 0);
    }
    if (transaction?.Withdraw && summary?.Withdraw) {
      summary.Withdraw.volume = (summary.Withdraw.volume ?? 0) + (transaction.Withdraw.volume ?? 0);
      summary.Withdraw.volumeUSD =
        (summary.Withdraw.volumeUSD ?? 0) + (transaction.Withdraw.volumeUSD ?? 0);
    }
  });

  return result;
};

/**
 * Aggregate totals per token only for selected accounts. Returns only
 * `selected-summary_<token>` keys. If `selection` is empty returns `{}`.
 */
export const aggregateSelectedTransactionSummary = (
  objOfTransaction: TransactionObj,
  selection: string[],
): TransactionObj => {
  if (!selection || selection.length === 0) return {};

  const result: TransactionObj = {};

  Object.entries(objOfTransaction).forEach(([key, transaction]) => {
    if (!key) return;
    if (key.startsWith('summary_') || key.startsWith('selected-summary_')) return;

    const parts = key.split('_');
    const accountId = parts[0];
    const token = parts.slice(1).join('_');
    if (!token || !accountId) return;

    if (!selection.includes(accountId)) return;

    const selectedKey = `selected-summary_${token}`;

    if (!result[selectedKey]) {
      result[selectedKey] = {
        Deposit: { volume: 0, volumeUSD: 0, currency: token },
        Withdraw: { volume: 0, volumeUSD: 0, currency: token },
      };
    }

    const sel = result[selectedKey];
    if (transaction?.Deposit && sel?.Deposit) {
      sel.Deposit.volumeUSD = (sel.Deposit.volumeUSD ?? 0) + (transaction.Deposit.volumeUSD ?? 0);
      sel.Deposit.volume = (sel.Deposit.volume ?? 0) + (transaction.Deposit.volume ?? 0);
    }
    if (transaction?.Withdraw && sel?.Withdraw) {
      sel.Withdraw.volume = (sel.Withdraw.volume ?? 0) + (transaction.Withdraw.volume ?? 0);
      sel.Withdraw.volumeUSD =
        (sel.Withdraw.volumeUSD ?? 0) + (transaction.Withdraw.volumeUSD ?? 0);
    }
  });

  return result;
};

/**
 * Takes a flat `TransactionObj` (keyed as `${accountID}_${token}`) and aggregates
 * totals into `summary_${token}` rows. When `selection` is non-empty only the
 * selected accounts are counted, and the totals are additionally written into
 * `selected-summary_${token}` so that the comparison table can render them under
 * the correct row key.
 */
export const aggregateTransactionSummary = (
  objOfTransaction: TransactionObj,
  selection: string[],
): TransactionObj => {
  // Keep backward-compatible behaviour: return original per-account entries
  // plus `summary_...` and `selected-summary_...` keys. Use the new helpers
  // to ensure totals are memoizable/separable.
  const totals = aggregateTransactionTotals(objOfTransaction);
  const selected = aggregateSelectedTransactionSummary(objOfTransaction, selection);

  return Object.assign({}, objOfTransaction, totals, selected);
};

const getTooltip = (
  startValue: string,
  endValue: string,
  depositValue: string,
  withdrawValue: string,
) => {
  const parts = [startValue, depositValue, withdrawValue].filter(Boolean);
  return `${endValue} - (${parts.length ? parts.join(' + ') : '0'})`;
};

export const getNetChangeValue = (
  startItemValue = 0,
  endItemValue = 0,
  depositValue: number,
  withdrawValue: number,
  token: string,
  startUnrealizedPnL = 0,
  endUnrealizedPnL = 0,
  isDerivative = false,
  startPrice = 0,
  endPrice = 0,
) => {
  let startValue = startItemValue;
  let endValue = endItemValue;

  if (isDerivative) {
    startValue = startUnrealizedPnL;
    endValue = endUnrealizedPnL;
    token = 'USD';
  }

  let diffValue = endValue - (startValue + depositValue + withdrawValue);

  if (isDerivative) {
    diffValue = (endValue - (startValue + depositValue + withdrawValue)) / endPrice;
  }

  const formatValues = (value: number, currency: string) =>
    formatNumber(value, { ...defaultFormat, currency });

  return {
    diffValue,
    volumeHint: getTooltip(
      startValue ? formatValues(startValue, token) : '',
      formatValues(endValue, token),
      depositValue ? formatValues(depositValue, token) : '',
      withdrawValue ? formatValues(withdrawValue, token) : '',
    ),
    volumeUSDHint: getTooltip(
      startValue ? formatValues(startValue, 'USD') : '',
      formatValues(endValue, 'USD'),
      depositValue ? formatValues(depositValue, 'USD') : '',
      withdrawValue ? formatValues(withdrawValue, 'USD') : '',
    ),
  };
};
